<template>
  <section class="payout-console go-ui" aria-label="Payouts a organizadores">
    <header class="payout-header">
      <div>
        <span class="eyebrow">Administración / Finanzas</span>
        <h2>Payouts</h2>
        <p>Saldos por evento y transferencias a organizadores.</p>
      </div>
      <FomoButton
        variant="secondary"
        icon="fas fa-sync-alt"
        class="p-button"
        aria-label="Actualizar saldos"
        :disabled="loading || saving || !!busyOrderId"
        @click="loadData"
        type="button"
        >Actualizar saldos</FomoButton
      >
    </header>
    <div v-if="feedback" :class="['p-feedback', { error: feedbackType === 'error' }]" role="status">
      {{ feedback }}
    </div>
    <div class="p-layout" :aria-busy="loading">
      <aside class="p-events fomo-dashboard-card" aria-label="Seleccionar evento">
        <div class="section-heading">
          <h3>Eventos</h3>
          <small>{{ filteredSettlements.length }}</small>
        </div>
        <label class="p-search"
          ><span class="sr-only">Buscar evento u organizador</span
          ><input v-model="search" type="search" placeholder="Buscar evento u organizador"
        /></label>
        <label class="p-filter"
          ><span class="sr-only">Estado del saldo</span
          ><select v-model="statusFilter">
            <option value="">Todos los saldos</option>
            <option value="available_to_order">Con disponible</option>
            <option value="held_amount">Con retención</option>
            <option value="reserved_amount">Con pagos en proceso</option>
          </select></label
        >
        <div v-if="loading && !settlements.length" class="p-empty">Cargando eventos…</div>
        <div v-else-if="!filteredSettlements.length" class="p-empty">No hay eventos para esta búsqueda.</div>
        <div class="p-event-list">
          <button
            v-for="s in filteredSettlements"
            :key="s.id"
            :class="['p-event', { selected: selectedSettlement && selectedSettlement.id === s.id }]"
            :aria-pressed="!!selectedSettlement && selectedSettlement.id === s.id"
            @click="selectSettlement(s)"
          >
            <span class="p-event-date"
              >{{ date(s.event_start_date, false) }} <small>{{ s.currency }}</small></span
            ><strong>{{ eventName(s) }}</strong
            ><small class="p-event-host">{{ s.organizer_name || `Organizador #${s.organizer}` }}</small
            ><span class="p-event-balance"
              >Disponible <b>{{ money(s.available_to_order, s.currency) }}</b></span
            ><span class="p-event-held"
              >Retenido <b>{{ money(s.held_amount, s.currency) }}</b></span
            >
          </button>
        </div>
      </aside>
      <div class="p-main">
        <article v-if="selectedSettlement" class="p-detail fomo-dashboard-card">
          <header class="p-detail-heading">
            <div>
              <span class="eyebrow">Balance del evento · {{ selectedSettlement.currency }}</span>
              <h3>{{ eventName(selectedSettlement) }}</h3>
              <p>{{ selectedSettlement.organizer_name || `Organizador #${selectedSettlement.organizer}` }}</p>
            </div>
            <a :href="`/event-host/${selectedSettlement.event}?section=finanzas`">Ver finanzas ↗</a>
          </header>
          <div class="p-totals">
            <div v-for="metric in metrics" :key="metric.key" class="fomo-dashboard-card">
              <span><i :class="metric.tone"></i>{{ metric.label }}</span
              ><strong>{{ money(selectedSettlement[metric.key], selectedSettlement.currency) }}</strong>
            </div>
          </div>
          <div class="p-net">
            <span>Neto del organizador</span
            ><strong>{{
              money(selectedSettlement.organizer_payable_base, selectedSettlement.currency)
            }}</strong>
          </div>
          <div v-if="selectedSettlement.blocked_reason" class="p-feedback error">
            {{ selectedSettlement.blocked_reason }}
          </div>
          <div class="p-retention">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="5" y="10" width="14" height="11" rx="3" />
              <path d="M8 10V7a4 4 0 0 1 8 0v3m-4 4v3" />
            </svg>
            <div>
              <strong>{{
                Number(selectedSettlement.held_amount) > 0 ? 'Retención del evento' : 'Sin saldo retenido'
              }}</strong>
              <p>{{ retentionCopy }}</p>
            </div>
          </div>
          <section class="p-sources" aria-labelledby="p-sources-title">
            <div class="section-heading">
              <div>
                <h4 id="p-sources-title">Por método de cobro</h4>
                <p>Cómo se distribuye el dinero del organizador.</p>
              </div>
              <small>{{ buckets.length }} métodos</small>
            </div>
            <div v-if="!buckets.length" class="p-empty">Todavía no hay un desglose por método.</div>
            <div v-for="bucket in buckets" :key="bucket.id" class="p-source fomo-dashboard-card">
              <header>
                <div class="p-source-identity">
                  <span class="p-source-icon" aria-hidden="true">{{
                    sourceMark(bucket.source_provider)
                  }}</span>
                  <div>
                    <strong>{{ sourceName(bucket.source_provider) }}</strong
                    ><small>{{ bucket.payments_count || 0 }} pagos · {{ bucket.currency }}</small>
                  </div>
                </div>
                <div class="p-source-net">
                  <small>Neto</small><strong>{{ money(bucket.net_payable_amount, bucket.currency) }}</strong>
                </div>
              </header>
              <div
                class="p-track"
                role="img"
                :aria-label="`Distribución de ${sourceName(bucket.source_provider)}`"
              >
                <span
                  v-for="segment in sourceSegments(bucket)"
                  :key="segment.key"
                  :class="segment.tone"
                  :style="{ width: segment.width + '%' }"
                ></span>
              </div>
              <dl class="p-source-amounts">
                <div v-for="metric in metrics" :key="metric.key">
                  <dt>{{ metric.short || metric.label }}</dt>
                  <dd>{{ money(bucket[metric.key], bucket.currency) }}</dd>
                </div>
              </dl>
            </div>
            <p v-if="hasStripe" class="p-provider-note">
              <strong>Saldo de Stripe.</strong> El disponible en FOMO puede diferir del saldo liquidado en
              Stripe. Los cobros pendientes de liquidación todavía no se pueden transferir al banco.
            </p>
          </section>
          <section class="p-safe" aria-labelledby="p-safe-title">
            <div class="p-safe-identity">
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="m12 3 8 3v6c0 4-4 7-8 9-4-2-8-5-8-9V6l8-3Z" />
                <path d="m8 12 3 3 5-6" />
              </svg>
              <div>
                <h4 id="p-safe-title">
                  FOMO Safe
                  <span>{{
                    selectedSettlement.fomo_safe && selectedSettlement.fomo_safe.enabled
                      ? 'Activo'
                      : 'Desactivado'
                  }}</span>
                </h4>
                <p>Protección adicional comprada por los asistentes.</p>
              </div>
            </div>
            <div class="p-safe-amount">
              <small>Cobrado en pagos aprobados</small><strong>{{ safeAmount }}</strong>
            </div>
            <p class="p-safe-explanation">
              Es un ingreso de FOMO. No forma parte del neto del organizador ni del saldo retenido de arriba.
            </p>
          </section>
          <div v-if="!draftOpen" class="p-new">
            <span>Prepara una orden con el saldo liberado.</span
            ><FomoButton
              variant="primary"
              icon="fas fa-arrow-right"
              class="p-button p-button--primary"
              :disabled="!canPrepare"
              @click="openDraft"
              type="button"
              >Nueva transferencia</FomoButton
            >
          </div>
          <form v-else class="p-draft" @submit.prevent="createOrder">
            <header class="section-heading">
              <div>
                <h4>Nueva transferencia</h4>
                <p>1. Crear borrador → 2. Aprobar → 3. Enviar</p>
              </div>
              <button
                class="p-button p-button--text"
                type="button"
                :disabled="saving"
                @click="draftOpen = false"
              >
                Cerrar
              </button>
            </header>
            <div class="p-form-grid">
              <label class="p-field go-field"
                ><span>Origen del dinero</span
                ><select v-model="draft.funding_bucket_id" :disabled="saving" @change="changeBucket">
                  <option v-for="b in buckets" :key="b.id" :value="String(b.id)">
                    {{ sourceName(b.source_provider) }}
                  </option>
                </select></label
              >
              <label class="p-field go-field"
                ><span>Cómo se enviará</span
                ><select v-model="draft.rail" :disabled="saving">
                  <option v-if="draft.funding_source === 'stripe_connect'" value="stripe_connect">
                    Stripe Connect · al banco
                  </option>
                  <option value="manual_bank_transfer">Transferencia bancaria manual</option>
                  <option value="manual_external_provider">Otro proveedor · registro manual</option>
                </select></label
              >
              <label class="p-field go-field"
                ><span>Monto en {{ selectedSettlement.currency }}</span
                ><input
                  v-model="draft.amount"
                  type="number"
                  inputmode="decimal"
                  min="0.01"
                  step="0.01"
                  :max="draftAvailable"
                  required
                  :disabled="saving"
                  :aria-invalid="!!amountError"
                  aria-describedby="p-amount-help"
                /><small id="p-amount-help" :class="{ 'p-error': amountError }">{{
                  amountError ||
                  `Disponible para esta orden: ${money(draftAvailable, selectedSettlement.currency)}`
                }}</small></label
              >
              <label class="p-field go-field"
                ><span>Nota interna <small>Opcional</small></span
                ><input
                  v-model="draft.comment"
                  maxlength="255"
                  placeholder="Referencia para el equipo"
                  :disabled="saving"
              /></label>
            </div>
            <footer class="p-draft-footer">
              <span>El borrador no envía dinero ni reserva saldo.</span
              ><FomoButton
                variant="primary"
                icon="fas fa-arrow-right"
                class="p-button p-button--primary"
                type="submit"
                :disabled="saving || !!amountError || !selectedBucket || Number(draft.amount) <= 0"
                >{{ saving ? 'Creando…' : 'Crear borrador' }}</FomoButton
              >
            </footer>
          </form>
        </article>
        <div v-else class="p-detail p-empty fomo-dashboard-card">
          {{ loading ? 'Cargando balances…' : 'Selecciona un evento para ver su balance.' }}
        </div>
        <section class="p-history fomo-dashboard-card" aria-labelledby="p-history-title">
          <header class="section-heading">
            <div>
              <h3 id="p-history-title">Transferencias</h3>
              <p>
                {{ showAllOrders ? 'Historial de todos los eventos.' : 'Historial del evento seleccionado.' }}
              </p>
            </div>
            <button class="p-button p-button--text" @click="showAllOrders = !showAllOrders">
              {{ showAllOrders ? 'Solo este evento' : 'Todos los eventos' }}
            </button>
          </header>
          <div v-if="!visibleOrders.length" class="p-empty history-empty">
            <strong>Aún no hay transferencias</strong>
            <p>Las órdenes que prepares para este evento aparecerán aquí.</p>
          </div>
          <article v-for="order in visibleOrders" :key="order.id" class="p-order">
            <div class="p-order-main">
              <div>
                <div class="p-order-meta">
                  <span>#{{ order.id }}</span
                  ><span :class="['p-status', order.status]">{{ statusName(order.status) }}</span>
                </div>
                <h4>{{ order.event_name || `Evento #${order.event}` }}</h4>
                <p>
                  {{ railName(order.rail) }} · {{ date(order.paid_at || order.sent_at || order.created_at) }}
                </p>
              </div>
              <strong class="p-order-amount">{{ money(order.amount, order.currency) }}</strong>
            </div>
            <p v-if="order.comment" class="p-reference">{{ order.comment }}</p>
            <p v-if="order.provider_reference || order.external_reference" class="p-reference">
              Referencia: {{ order.external_reference || order.provider_reference }}
            </p>
            <p v-if="order.failure_reason" class="p-error">{{ order.failure_reason }}</p>
            <div class="p-order-actions">
              <span
                v-if="isApiOrder(order) && ['submitted', 'processing'].includes(order.status)"
                class="p-waiting"
                >Esperando confirmación de Stripe</span
              ><FomoButton
                variant="secondary"
                icon=""
                v-if="order.status === 'draft'"
                class="p-button"
                :disabled="!!busyOrderId"
                @click="transition(order, 'approve')"
                type="button"
                >Aprobar</FomoButton
              ><FomoButton
                variant="primary"
                icon="fas fa-arrow-right"
                v-if="order.status === 'approved'"
                class="p-button p-button--primary"
                :disabled="!!busyOrderId"
                @click="transition(order, 'submit')"
                type="button"
                >{{ isApiOrder(order) ? 'Enviar a Stripe' : 'Registrar envío' }}</FomoButton
              >
              <template
                v-if="!isApiOrder(order) && ['approved', 'submitted', 'processing'].includes(order.status)"
                ><label class="p-proof"
                  >Adjuntar comprobante<input
                    type="file"
                    accept="image/*,.pdf"
                    :disabled="!!busyOrderId"
                    @change="setProofFile(order, $event)" /></label
                ><FomoButton
                  variant="secondary"
                  icon=""
                  class="p-button"
                  :disabled="!!busyOrderId"
                  @click="markPaid(order)"
                  type="button"
                  >Confirmar pago</FomoButton
                ><button
                  class="p-button p-button--text"
                  :disabled="!!busyOrderId"
                  @click="transition(order, 'fail')"
                >
                  Registrar fallo
                </button></template
              >
              <button
                v-if="
                  ['draft', 'approved'].includes(order.status) ||
                  (!isApiOrder(order) && ['submitted', 'processing'].includes(order.status))
                "
                class="p-button p-button--text"
                :disabled="!!busyOrderId"
                @click="transition(order, 'cancel')"
              >
                Cancelar orden
              </button>
            </div>
          </article>
        </section>
      </div>
    </div>
  </section>
</template>

<script>
import { apiService, apiServiceForm } from '../../common/api.services.js'
import '../../styles/go-workspace.css'
import '../../styles/fomo-dashboard.css'
import FomoButton from '../../components/ui/FomoButton.vue'
import { formatCurrencyAmount } from '../../common/currency.js'

export default {
  name: 'PayoutOpsView',
  components: { FomoButton },
  data() {
    return {
      settlements: [],
      orders: [],
      loading: false,
      saving: false,
      busyOrderId: null,
      search: '',
      statusFilter: '',
      selectedSettlement: null,
      feedback: '',
      feedbackType: 'success',
      proofFiles: {},
      draftOpen: false,
      showAllOrders: false,
      draft: {
        funding_bucket_id: '',
        rail: 'manual_bank_transfer',
        funding_source: '',
        amount: '',
        comment: '',
      },
      metrics: [
        { key: 'available_to_order', label: 'Disponible en FOMO', short: 'Disponible', tone: 'available' },
        { key: 'held_amount', label: 'Retenido', tone: 'held' },
        { key: 'reserved_amount', label: 'En proceso', tone: 'reserved' },
        { key: 'paid_amount', label: 'Pagado', tone: 'paid' },
      ],
    }
  },
  computed: {
    filteredSettlements() {
      const query = this.search.trim().toLocaleLowerCase()
      return this.settlements.filter(
        (s) =>
          (!query ||
            `${s.event_name || ''} ${s.organizer_name || ''} ${s.event} ${s.organizer}`
              .toLocaleLowerCase()
              .includes(query)) &&
          (!this.statusFilter || Number(s[this.statusFilter]) > 0)
      )
    },
    buckets() {
      return this.selectedSettlement ? this.selectedSettlement.funding_buckets || [] : []
    },
    selectedBucket() {
      return this.buckets.find((b) => String(b.id) === String(this.draft.funding_bucket_id)) || null
    },
    draftAvailable() {
      return this.selectedBucket
        ? Math.max(
            0,
            Math.min(
              Number(this.selectedBucket.available_to_order) || 0,
              Number(this.selectedSettlement.available_to_order) || 0
            )
          )
        : 0
    },
    amountError() {
      if (this.draft.amount === '') return ''
      const amount = Number(this.draft.amount)
      if (!Number.isFinite(amount) || amount <= 0) return 'Ingresa un monto mayor a cero.'
      if (amount > this.draftAvailable) return 'El monto supera el disponible de este método.'
      if (Math.abs(amount * 100 - Math.round(amount * 100)) > 0.000001)
        return 'Usa como máximo dos decimales.'
      return ''
    },
    canPrepare() {
      return (
        !this.loading &&
        this.buckets.some((b) => Number(b.available_to_order) > 0) &&
        Number(this.selectedSettlement.available_to_order) > 0 &&
        this.selectedSettlement.status !== 'blocked'
      )
    },
    hasStripe() {
      return this.buckets.some((b) => ['stripe', 'stripe_connect'].includes(b.source_provider))
    },
    safeAmount() {
      return this.selectedSettlement && this.selectedSettlement.fomo_safe
        ? this.money(this.selectedSettlement.fomo_safe.amount, this.selectedSettlement.currency)
        : 'Sin información'
    },
    retentionCopy() {
      if (Number(this.selectedSettlement.held_amount) <= 0)
        return 'El saldo restante está liberado o asignado a transferencias.'
      const policy = this.selectedSettlement.release_policy
      if (!policy) return 'Este saldo todavía no está disponible para transferencias.'
      const percent = policy.mode === 'split_60_40_event' ? Number(policy.pre_event_cap_percent) : 0
      return `${percent}% antes del evento · ${100 - percent}% después de su cierre y ${
        policy.post_event_buffer_hours
      } h de espera.${
        policy.scheduled_release_at ? ` Liberación prevista: ${this.date(policy.scheduled_release_at)}.` : ''
      }`
    },
    visibleOrders() {
      return this.showAllOrders
        ? this.orders
        : this.selectedSettlement
        ? this.orders.filter(
            (o) =>
              o.event === this.selectedSettlement.event && o.currency === this.selectedSettlement.currency
          )
        : []
    },
  },
  created() {
    this.loadData()
  },
  methods: {
    money(value, currency = 'USD') {
      return formatCurrencyAmount(value, currency)
    },
    date(value, withTime = true) {
      if (!value || Number.isNaN(new Date(value).getTime())) return 'Sin fecha'
      return new Intl.DateTimeFormat('es-EC', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
      }).format(new Date(value))
    },
    eventName(s) {
      return s.event_name || `Evento #${s.event}`
    },
    sourceName(source) {
      return (
        {
          stripe_connect: 'Stripe Connect',
          stripe: 'Tarjeta · Stripe',
          bank_transfer: 'Transferencia bancaria',
          cash: 'Efectivo',
          fomo_cash: 'Efectivo (recibe Fomoplanes)',
          fomo_pos: 'POS (Gestionada por Fomoplanes)',
          paypal: 'PayPal',
          rebill: 'Rebill',
          other: 'Otros cobros',
        }[source] || source
      )
    },
    sourceMark(source) {
      return (
        {
          stripe_connect: 'SC',
          stripe: 'ST',
          bank_transfer: 'BT',
          cash: '$',
          fomo_cash: '$',
          fomo_pos: 'POS',
          paypal: 'PP',
          rebill: 'RB',
          other: 'OT',
        }[source] || '—'
      )
    },
    railName(rail) {
      return (
        {
          stripe_connect: 'Stripe Connect',
          stripe_global_payouts_api: 'Stripe Global Payouts',
          manual_bank_transfer: 'Transferencia bancaria manual',
          manual_external_provider: 'Proveedor externo',
        }[rail] || rail
      )
    },
    statusName(status) {
      return (
        {
          draft: 'Borrador',
          approved: 'Aprobada',
          submitted: 'Enviada',
          processing: 'En proceso',
          paid: 'Pagada',
          failed: 'Fallida',
          cancelled: 'Cancelada',
        }[status] || status
      )
    },
    isApiOrder(order) {
      return ['stripe_connect', 'stripe_global_payouts_api'].includes(order.rail)
    },
    sourceSegments(bucket) {
      const total = this.metrics.reduce((sum, m) => sum + Math.max(0, Number(bucket[m.key]) || 0), 0)
      return this.metrics.map((m) => ({
        ...m,
        width: total ? (Math.max(0, Number(bucket[m.key]) || 0) / total) * 100 : 0,
      }))
    },
    extractError(payload, fallback) {
      if (!payload) return fallback
      if (typeof payload === 'string') return payload.startsWith('<') ? fallback : payload
      const detail = payload.err || payload.detail
      if (detail) return Array.isArray(detail) ? detail.join(' ') : String(detail)
      const values = Object.values(payload).filter((v) => typeof v === 'string' || Array.isArray(v))
      return values.length ? values.flat().join(' ') : fallback
    },
    async loadData() {
      this.loading = true
      try {
        const [settlements, orders] = await Promise.all([
          apiService('/api/admin/payouts/settlements/'),
          apiService('/api/admin/payouts/orders/'),
        ])
        if (!Array.isArray(settlements))
          throw new Error(this.extractError(settlements, 'No pudimos cargar los balances.'))
        if (!Array.isArray(orders))
          throw new Error(this.extractError(orders, 'No pudimos cargar las transferencias.'))
        const currentId = this.selectedSettlement && this.selectedSettlement.id
        this.settlements = settlements
        this.orders = orders
        const selection = settlements.find((s) => s.id === currentId)
        if (selection) this.selectedSettlement = selection
        else this.selectSettlement(settlements[0] || null)
        if (this.draftOpen && !this.selectedBucket) this.changeBucket()
      } catch (error) {
        this.feedback = error.message || 'No pudimos actualizar los saldos.'
        this.feedbackType = 'error'
      } finally {
        this.loading = false
      }
    },
    selectSettlement(settlement) {
      this.selectedSettlement = settlement
      this.draftOpen = false
      this.draft.comment = ''
      this.draft.funding_bucket_id = ''
      this.changeBucket()
    },
    changeBucket() {
      const bucket =
        this.selectedBucket || this.buckets.find((b) => Number(b.available_to_order) > 0) || this.buckets[0]
      this.draft.funding_bucket_id = bucket ? String(bucket.id) : ''
      this.draft.funding_source = bucket ? bucket.source_provider : ''
      this.draft.rail =
        bucket && bucket.source_provider === 'stripe_connect' ? 'stripe_connect' : 'manual_bank_transfer'
      this.draft.amount = this.draftAvailable > 0 ? this.draftAvailable.toFixed(2) : ''
    },
    openDraft() {
      this.changeBucket()
      this.draftOpen = true
    },
    async createOrder() {
      if (
        !this.selectedSettlement ||
        !this.selectedBucket ||
        this.amountError ||
        Number(this.draft.amount) <= 0 ||
        this.saving
      )
        return
      this.saving = true
      try {
        const data = await apiService('/api/admin/payouts/orders/', 'POST', {
          settlement_id: this.selectedSettlement.id,
          funding_bucket_id: Number(this.draft.funding_bucket_id),
          rail: this.draft.rail,
          funding_source: this.draft.funding_source,
          execution_mode: this.draft.rail === 'stripe_connect' ? 'api' : 'manual',
          amount: this.draft.amount,
          comment: this.draft.comment,
        })
        if (!data || !data.id) throw new Error(this.extractError(data, 'No pudimos crear el borrador.'))
        this.feedback = `Borrador #${data.id} creado. El siguiente paso es aprobarlo en Transferencias.`
        this.feedbackType = 'success'
        this.draftOpen = false
        this.draft.comment = ''
        await this.loadData()
      } catch (error) {
        this.feedback = error.message
        this.feedbackType = 'error'
      } finally {
        this.saving = false
      }
    },
    async transition(order, action) {
      if (this.busyOrderId) return
      this.busyOrderId = order.id
      try {
        const data = await apiService(
          `/api/admin/payouts/orders/${order.id}/${action}/`,
          'POST',
          {},
          { timeoutMs: 60000 }
        )
        if (!data || !data.id) throw new Error(this.extractError(data, 'No pudimos completar la acción.'))
        this.feedback = `Orden #${order.id}: ${this.statusName(data.status).toLocaleLowerCase()}.`
        this.feedbackType = 'success'
        await this.loadData()
      } catch (error) {
        this.feedback = error.message
        this.feedbackType = 'error'
        await this.loadData()
      } finally {
        this.busyOrderId = null
      }
    },
    setProofFile(order, event) {
      this.proofFiles = { ...this.proofFiles, [order.id]: event.target.files[0] || null }
    },
    async markPaid(order) {
      if (this.busyOrderId) return
      const reference = window.prompt(
        'Referencia de la transferencia ya realizada',
        order.external_reference || ''
      )
      if (reference === null) return
      this.busyOrderId = order.id
      try {
        const payload = new FormData()
        payload.append('external_reference', reference)
        if (this.proofFiles[order.id]) payload.append('proof_file', this.proofFiles[order.id])
        const data = await apiServiceForm(
          `/api/admin/payouts/orders/${order.id}/mark-paid/`,
          'POST',
          payload,
          { timeoutMs: 60000 }
        )
        if (!data || !data.id) throw new Error(this.extractError(data, 'No pudimos registrar el pago.'))
        this.feedback = `Pago de la orden #${order.id} confirmado.`
        this.feedbackType = 'success'
        await this.loadData()
      } catch (error) {
        this.feedback = error.message
        this.feedbackType = 'error'
      } finally {
        this.busyOrderId = null
      }
    },
  },
}
</script>

<style scoped>
.payout-console {
  --p-accent: var(--purple-bright, #6f5a98);
  --p-accent-soft: var(--purple-soft, #44365e);
  --p-deep: var(--purple-deep, #181320);
  --p-accent-text: #cfd8ea;
  --p-muted: rgba(198, 207, 226, 0.8);
  --p-text: #f4f7ff;
  --p-border: var(--fomo-border);
  --p-card: var(--fomo-surface);
  --p-control: var(--fomo-control);
  --p-available: #a083ca;
  --p-held: #574366;
  --p-reserved: #bca6d7;
  --p-paid: #79618f;
  color: var(--p-text);
  min-width: 0;
}
.payout-console h2,
.payout-console h3,
.payout-console h4,
.payout-console p {
  margin: 0;
}
.payout-console h4 {
  font-size: 1rem;
  font-weight: 700;
}
.payout-console small {
  font-size: 0.74rem;
}
.payout-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  padding: 8px 0 24px;
}
.payout-header h2 {
  margin: 7px 0 9px;
  font-size: clamp(1.9rem, 3vw, 2.6rem);
  line-height: 1.1;
}
.payout-header p {
  color: var(--p-muted);
  font-size: 0.87rem;
}
.payout-console .eyebrow {
  font: 600 0.66rem var(--fomo-font-label);
  color: var(--p-muted);
  letter-spacing: 0.14em;
  text-transform: uppercase;
}
.payout-console .p-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-height: 44px;
  padding: 0;
  font-family: var(--fomo-font-button);
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  filter: grayscale(1);
}
.payout-console .p-button--primary {
  outline: 1px solid rgba(208, 218, 236, 0.34);
  outline-offset: -1px;
}
.payout-console .p-button--text {
  padding: 8px 10px;
  background: transparent;
  border-color: transparent;
  box-shadow: none;
  color: var(--p-muted);
}
.payout-console .p-button:hover:not(:disabled) {
  filter: grayscale(1) brightness(1.12);
}
.payout-console :is(button, a, input, select):focus-visible {
  outline: 2px solid #cfd6e0;
  outline-offset: 3px;
}
.p-totals strong,
.p-source-amounts dd,
.p-event-balance b,
.p-event-held b {
  font-variant-numeric: tabular-nums;
}
.p-totals span,
.p-source-amounts dt,
.p-event-date,
.p-source-net small {
  font-family: var(--fomo-font-label);
}
.p-layout {
  display: grid;
  grid-template-columns: minmax(230px, 280px) minmax(0, 1fr);
  gap: 22px;
  align-items: start;
}
.p-events {
  padding: 18px 14px;
}
.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}
.section-heading h3 {
  font-size: 1.15rem;
  font-weight: 700;
}
.section-heading p {
  margin-top: 5px;
  color: var(--p-muted);
  font-size: 0.78rem;
}
.section-heading > small {
  color: var(--p-muted);
}
.p-search,
.p-filter {
  display: block;
  margin-bottom: 9px;
}
.p-search input,
.p-filter select {
  width: 100%;
  min-height: 40px;
  padding: 10px 11px;
  border: 1px solid var(--p-border);
  border-radius: 12px;
  background: var(--p-control);
  color: var(--p-text);
  font-size: 0.76rem;
}
.p-search input::placeholder {
  color: var(--p-muted);
}
.p-filter select option {
  background: #17151e;
}
.p-event-list {
  display: grid;
  gap: 8px;
  margin-top: 18px;
  max-height: 720px;
  overflow: auto;
}
.p-event {
  display: grid;
  width: 100%;
  gap: 7px;
  text-align: left;
  padding: 16px 13px;
  border: 1px solid transparent;
  border-radius: 15px;
  color: var(--p-text);
  background: transparent;
}
.p-event:hover {
  background: rgba(111, 90, 152, 0.05);
  border-color: var(--p-border);
}
.p-event.selected {
  background: var(--fomo-control);
  border-color: rgba(196, 206, 228, 0.24);
}
.p-event-date {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  color: var(--p-muted);
  font-size: 0.67rem;
}
.p-event-date small {
  font: 600 0.61rem var(--fomo-font-label);
}
.p-event > strong {
  font-family: var(--fomo-font-heading);
  font-size: 0.99rem;
  line-height: 1.35;
  letter-spacing: -0.03em;
}
.p-event-host {
  color: var(--p-muted);
  overflow-wrap: anywhere;
}
.p-event-balance,
.p-event-held {
  display: flex;
  justify-content: space-between;
  gap: 6px;
  font-size: 0.67rem;
  color: var(--p-muted);
}
.p-event-balance {
  padding-top: 9px;
  margin-top: 4px;
  border-top: 1px solid var(--p-border);
}
.p-event-balance b {
  color: var(--p-accent-text);
  font-weight: 600;
}
.p-event-held b {
  color: var(--p-accent-text);
  font-weight: 500;
}
.p-main {
  min-width: 0;
  display: grid;
  gap: 22px;
}
.p-detail,
.p-history {
  padding: 26px;
}
.p-detail-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 24px;
}
.p-detail-heading h3 {
  margin: 8px 0 7px;
  font-size: clamp(1.3rem, 2.2vw, 1.8rem);
  line-height: 1.15;
}
.p-detail-heading p {
  font-size: 0.8rem;
  color: var(--p-muted);
}
.p-detail-heading > a {
  color: var(--p-accent-text);
  font-size: 0.75rem;
  white-space: nowrap;
}
.p-totals {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  padding: 0;
}
.p-totals > div {
  display: grid;
  gap: 12px;
  padding: 17px 14px;
}
.p-totals span {
  display: flex;
  align-items: center;
  gap: 7px;
  color: var(--p-muted);
  font-size: 0.66rem;
}
.p-totals i {
  display: inline-block;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  flex-shrink: 0;
}
.p-totals strong {
  color: var(--p-text);
  font-family: var(--fomo-font-label);
  font-size: clamp(0.95rem, 1.45vw, 1.25rem);
  font-weight: 600;
  letter-spacing: -0.025em;
  white-space: nowrap;
}
.available {
  background: var(--p-available);
}
.held {
  background: var(--p-held);
}
.reserved {
  background: var(--p-reserved);
}
.paid {
  background: var(--p-paid);
}
.p-net {
  display: flex;
  justify-content: space-between;
  padding: 12px 0 0;
  font-size: 0.73rem;
  color: var(--p-muted);
}
.p-net strong {
  font-weight: 500;
  color: var(--p-accent-text);
}
.p-retention {
  display: flex;
  gap: 11px;
  margin: 22px 0 28px;
  padding: 13px 15px;
  border: 1px solid var(--p-border);
  border-radius: 14px;
  background: rgba(111, 90, 152, 0.04);
}
.p-retention svg {
  width: 17px;
  height: 19px;
  margin-top: 2px;
  stroke: var(--p-accent-text);
  stroke-width: 1.5;
  flex-shrink: 0;
}
.p-retention strong {
  color: var(--p-accent-text);
  font-size: 0.75rem;
  font-weight: 600;
}
.p-retention p {
  margin-top: 4px;
  color: var(--p-muted);
  font-size: 0.72rem;
  line-height: 1.65;
}
.p-sources .section-heading {
  margin-bottom: 14px;
}
.p-source {
  padding: 18px;
  margin-top: 11px;
}
.p-source header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}
.p-source-identity {
  display: flex;
  align-items: center;
  gap: 11px;
}
.p-source-identity > div {
  display: grid;
  gap: 2px;
}
.p-source-identity strong {
  font-size: 0.85rem;
  font-weight: 600;
}
.p-source-identity small {
  color: var(--p-muted);
  font-size: 0.66rem;
}
.p-source-icon {
  display: grid;
  place-items: center;
  height: 33px;
  width: 33px;
  border: 1px solid var(--p-border);
  border-radius: 10px;
  font: 600 0.7rem var(--fomo-font-label);
  color: var(--p-accent-text);
  background: var(--p-control);
}
.p-source-net {
  display: grid;
  text-align: right;
  gap: 2px;
}
.p-source-net small {
  color: var(--p-muted);
  font-size: 0.62rem;
}
.p-source-net strong {
  font: 500 0.85rem var(--fomo-font-label);
}
.p-track {
  display: flex;
  height: 4px;
  border-radius: 10px;
  overflow: hidden;
  background: rgba(111, 90, 152, 0.13);
  margin: 18px 0 14px;
  gap: 2px;
}
.p-track .held {
  background: repeating-linear-gradient(135deg, var(--p-held) 0, var(--p-held) 4px, #705686 4px, #705686 6px);
}
.p-track span {
  height: 100%;
}
.p-source-amounts {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin: 0;
}
.p-source-amounts dt {
  color: var(--p-muted);
  font-size: 0.63rem;
  font-weight: 400;
}
.p-source-amounts dd {
  color: var(--p-text);
  margin: 5px 0 0;
  font: 500 0.8rem var(--fomo-font-label);
  white-space: nowrap;
}
.p-provider-note {
  margin-top: 12px !important;
  padding: 0 2px;
  color: var(--p-muted);
  font-size: 0.71rem;
  line-height: 1.7;
}
.p-provider-note strong {
  font-weight: 600;
  color: #cdd6e4;
}
.p-safe {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 25px;
  padding: 20px 0;
  border-top: 1px solid var(--p-border);
  border-bottom: 1px solid var(--p-border);
}
.p-safe-identity {
  display: flex;
  align-items: center;
  gap: 12px;
}
.p-safe-identity svg {
  height: 26px;
  width: 24px;
  flex-shrink: 0;
  stroke: var(--p-accent-text);
  stroke-width: 1.4;
}
.p-safe h4 {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.98rem;
}
.p-safe h4 span {
  font: 500 0.58rem var(--fomo-font-label);
  color: var(--p-accent-text);
  padding: 3px 7px;
  border: 1px solid var(--p-border);
  border-radius: 6px;
  background: rgba(111, 90, 152, 0.08);
}
.p-safe-identity p {
  margin-top: 5px;
  font-size: 0.72rem;
  color: var(--p-muted);
}
.p-safe-amount {
  display: grid;
  gap: 5px;
  text-align: right;
}
.p-safe-amount small {
  color: var(--p-muted);
  font-size: 0.63rem;
}
.p-safe-amount strong {
  font: 600 1.03rem var(--fomo-font-label);
}
.p-safe-explanation {
  flex-basis: 100%;
  font-size: 0.69rem;
  line-height: 1.6;
  color: var(--p-muted);
}
.p-new {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding-top: 22px;
}
.p-new > span {
  color: var(--p-muted);
  font-size: 0.75rem;
}
.p-draft {
  padding-top: 25px;
}
.p-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 19px;
}
.p-field {
  min-width: 0;
}
.p-field > span {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}
.p-field small {
  color: var(--p-muted);
  font-size: 0.68rem;
}
.p-field input:invalid:not(:focus) {
  box-shadow: none;
}
.p-draft-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-top: 24px;
}
.p-draft-footer > span {
  color: var(--p-muted);
  font-size: 0.71rem;
}
.p-feedback {
  padding: 13px 17px;
  margin-bottom: 18px;
  border: 1px solid #b5a3cf40;
  border-radius: 14px;
  color: #d6c9e9;
  background: #b5a3cf09;
  font-size: 0.8rem;
}
.p-feedback.error {
  color: #e4b3ad;
  background: #e4b3ad08;
  border-color: #e4b3ad35;
}
.p-error {
  color: #e8b5ae !important;
  font-size: 0.75rem;
}
.p-empty {
  padding: 26px 15px;
  text-align: center;
  font-size: 0.8rem;
  color: var(--p-muted);
}
.history-empty {
  border: 1px dashed var(--p-border);
  border-radius: 16px;
}
.history-empty strong {
  color: var(--p-text);
  font-weight: 500;
}
.history-empty p {
  font-size: 0.73rem;
  margin-top: 8px;
}
.p-order {
  padding: 20px 0;
  border-top: 1px solid var(--p-border);
}
.p-order:last-child {
  padding-bottom: 0;
}
.p-order-main {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: center;
}
.p-order-meta {
  display: flex;
  align-items: center;
  gap: 9px;
  color: var(--p-muted);
  font: 0.67rem var(--fomo-font-label);
  margin-bottom: 10px;
}
.p-order h4 {
  font-size: 1.04rem;
  margin-bottom: 7px;
}
.p-order-main p {
  color: var(--p-muted);
  font-size: 0.7rem;
}
.p-order-amount {
  font: 600 1.07rem var(--fomo-font-label);
  white-space: nowrap;
}
.p-status {
  border: 1px solid var(--p-border);
  border-radius: 6px;
  padding: 3px 7px;
  background: rgba(111, 90, 152, 0.08);
  color: #cbd4e2;
  font-size: 0.62rem;
}
.p-status.processing,
.p-status.submitted {
  color: #bed5ec;
  border-color: #91b5dc30;
  background: #91b5dc0a;
}
.p-status.paid {
  color: #c0d6cb;
  border-color: #98baac35;
  background: #98baac0a;
}
.p-status.failed {
  color: #e4b3ad;
  border-color: #e4b3ad35;
}
.p-reference {
  margin-top: 12px !important;
  font-size: 0.68rem;
  color: var(--p-muted);
  overflow-wrap: anywhere;
}
.p-order-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-top: 14px;
}
.p-order-actions:empty {
  display: none;
}
.p-order-actions .p-button {
  min-height: 38px;
  padding: 0;
  font-size: 0.73rem !important;
}
.p-order-actions .p-button--text {
  padding: 7px 10px;
}
.p-waiting {
  color: #a5bad2;
  font-size: 0.72rem;
}
.p-proof {
  display: grid;
  gap: 6px;
  font-size: 0.7rem;
  color: var(--p-muted);
}
.p-proof input {
  max-width: 220px;
  font-size: 0.7rem;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
@media (max-width: 1100px) {
  .p-layout {
    grid-template-columns: 235px minmax(0, 1fr);
    gap: 16px;
  }
  .p-detail,
  .p-history {
    padding: 20px;
  }
  .p-totals {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }
  .p-source-amounts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 15px;
  }
  .p-safe-amount {
    text-align: left;
  }
  .p-safe-identity p {
    font-size: 0.7rem;
  }
  .p-detail-heading {
    align-items: flex-start;
  }
  .p-detail-heading > a {
    margin-top: 5px;
  }
}
@media (max-width: 760px) {
  .p-layout {
    grid-template-columns: 1fr;
  }
  .p-events {
    padding: 16px;
  }
  .p-event-list {
    grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
    max-height: 295px;
  }
  .payout-header {
    gap: 15px;
    align-items: flex-start;
  }
  .payout-header > .p-button {
    min-height: 44px;
    padding: 0;
    font-size: 0.74rem !important;
  }
  .payout-header h2 {
    font-size: 2rem;
  }
  .payout-header p {
    font-size: 0.76rem;
  }
  .p-detail-heading {
    flex-wrap: wrap;
    gap: 12px;
  }
  .p-detail-heading h3 {
    font-size: 1.4rem;
  }
  .p-detail-heading > a {
    margin: 0;
  }
  .p-source-amounts {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  .p-totals strong {
    color: var(--p-text);
    font-size: 1.18rem;
  }
}
@media (max-width: 500px) {
  .payout-header {
    flex-wrap: wrap;
  }
  .payout-header > .p-button {
    width: 100%;
    padding: 0;
  }
  .p-event-list {
    grid-template-columns: 1fr;
    max-height: 300px;
  }
  .p-detail,
  .p-history {
    padding: 18px 16px;
    border-radius: 20px;
  }
  .p-totals > div {
    padding: 15px 12px;
  }
  .p-source {
    padding: 15px 12px;
  }
  .p-source-identity {
    gap: 8px;
  }
  .p-source-icon {
    width: 29px;
    height: 29px;
  }
  .p-source-identity strong {
    font-size: 0.78rem;
  }
  .p-source-net strong {
    font-size: 0.76rem;
  }
  .p-source-amounts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .p-new,
  .p-draft-footer {
    flex-direction: column;
    align-items: stretch;
    gap: 13px;
  }
  .p-form-grid {
    grid-template-columns: 1fr;
  }
  .p-safe {
    gap: 15px;
  }
  .p-safe-amount {
    margin-left: 36px;
  }
  .p-safe h4 {
    font-size: 0.97rem;
  }
  .p-order-main {
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 10px;
  }
  .p-history > .section-heading {
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .p-history > .section-heading .p-button {
    min-height: 32px;
    padding: 6px 9px;
    font-size: 0.7rem !important;
  }
  .p-retention {
    padding: 12px;
  }
}
.p-button :deep(.fomo-hero__action-inner) {
  min-height: 40px;
  padding: 0 9px 0 16px;
  gap: 12px;
}
.p-button :deep(.fomo-hero__action-copy) {
  font-family: var(--fomo-font-button);
  font-size: inherit;
  line-height: 1.35;
}
.p-button :deep(.fomo-hero__action-icon) {
  height: 28px;
  width: 28px;
  flex-basis: 28px;
}
.p-button :deep(.fomo-hero__action-icon i) {
  font-size: 0.7rem;
}
.p-order-actions .p-button :deep(.fomo-hero__action-inner) {
  min-height: 34px;
  padding: 0 12px;
}
@media (max-width: 500px) {
  .p-totals strong {
    font-size: 1rem;
  }
}
</style>
